import { Container, Row } from "reactstrap";
import { SystemConfigLayout } from "../../components/SystemConfigLayout";
import { Toolbar } from "../../../components/Toolbar";
import { useCallback } from "react";
import { DESTROY, UPDATE, makeColumns, useAdministrateTable, useCreatePermission } from "../../utils";
import { AdministrateTable } from "../../components/AdministrateTable";
import { AddAutoStatusParsingSettingModal } from "./components/AddAutoStatusParsingSettingModal";
import { AutoStatusParsingSettingActionCell } from "./components/AutoStatusParsingSettingActionCell";
import { RerunSuperDialParser } from "./components/RerunSuperDialParser";
import { BoolCell } from "../../../components/grid/cells/BoolCell";
import { AdministrateEnumFilter } from "../../components/AdministrateEnumFilter";
import { EnumFilter } from "../../../components/inputs/EnumFilter";
import { StatusDisplayer, SubstatusDisplayer } from "../../submit_right/phone_responses/PhoneResponsesIndex";
import { RerunMedsenderAutoStatus } from "./components/RerunMedsenderAutoStatus";

const useColumns = makeColumns(() => [
  {
    id: "id",
    accessor: "id",
    Header: "ID",
  },
  {
    id: "message_regex",
    accessor: "messageRegex",
    Header: "Auto Status Parsing Setting",
  },
  {
    id: "assign_to_followup",
    accessor: "assignToFollowup",
    Header: "Assign to Follow Up",
    Cell: BoolCell,
    Filter: (props) => (
      <EnumFilter
        {...props}
        options={[
          { value: true, label: "Yes" },
          { value: false, label: "No" },
        ]}
      />
    ),
  },
  {
    id: "reassign_to_pas",
    accessor: "reassignToPas",
    Header: "Reassign To PAS",
    Cell: BoolCell,
    Filter: (props) => (
      <EnumFilter
        {...props}
        options={[
          { value: true, label: "Yes" },
          { value: false, label: "No" },
        ]}
      />
    ),
  },
  {
    id: "service",
    accessor: "service",
    Header: "Service",
    Filter: (props) => (
      <AdministrateEnumFilter
        {...props}
        resourceUrl="/admin/enums/auto_status_parsing_setting_services.json"
        valueKey="id"
        labelKey="label"
        searchParam="search"
      />
    ),
  },
  {
    id: "status",
    accessor: "status",
    Header: "Status",
    Cell: (props) => <StatusDisplayer status={props.value || "none"} />,
    Filter: (props) => (
      <AdministrateEnumFilter
        {...props}
        resourceUrl="/admin/enums/prior_auth_statuses.json"
        valueKey="id"
        labelKey="label"
        searchParam="search"
      />
    ),
  },
  {
    id: "substatus",
    accessor: "substatus",
    Header: "Sub Status",
    Cell: (props) => <SubstatusDisplayer substatus={props.value || "none"} />,
    Filter: (props) => (
      <AdministrateEnumFilter
        {...props}
        resourceUrl="/admin/enums/prior_auth_substatuses.json"
        valueKey="value"
        labelKey="label"
        searchParam="search"
      />
    ),
  },
  {
    id: "denial_reason_code",
    accessor: "denialReason",
    Header: "Denial Reason",
    Filter: (props) => (
      <AdministrateEnumFilter
        {...props}
        resourceUrl="/admin/enums/denial_reason_options.json"
        valueKey="id"
        labelKey="label"
        searchParam="search"
      />
    ),
  },
  {
    id: "automated_comment",
    accessor: "automatedComment",
    Header: "Automated Comment",
    disableFilters: true,
    disableSortBy: true,
  },
  {
    id: "actions",
    accessor: "Actions",
    Header: "Actions",
    Cell: AutoStatusParsingSettingActionCell,
    disableFilters: true,
    disableSortBy: true,
  },
]);

export const AutoStatusParsingSettings = () => {
  const { tableRef, setTableFilters } = useAdministrateTable();
  const columns = useColumns();
  const [canCreate] = useCreatePermission("AutoStatusParsingSetting");

  const onAdd = useCallback(
    (message, errorDetails = []) => {
      tableRef.current?.refresh?.();
      tableRef.current?.setMessage?.(message, errorDetails.length ? "warning" : "success", errorDetails);
    },
    [tableRef]
  );

  return (
    <SystemConfigLayout title="Auto Status Parsing Settings">
      <Container fluid>
        <Row className="align-items-center mb-3">
          <Toolbar className="ml-auto">
            {canCreate && <RerunMedsenderAutoStatus outline className="me-2" onRun={onAdd} />}
            {canCreate && <RerunSuperDialParser outline className="me-2" onRun={onAdd} />}
            {canCreate && <AddAutoStatusParsingSettingModal onAdd={onAdd} color="primary" />}
          </Toolbar>
        </Row>
      </Container>

      <AdministrateTable
        resourceUrl={"/admin/auto_status_parsing_settings.json"}
        resourceName={"auto_status_parsing_setting"}
        permissions={[DESTROY, UPDATE]}
        onFilteredChange={setTableFilters}
        columns={columns}
        ref={tableRef}
      />
    </SystemConfigLayout>
  );
};
